import React, { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useAgGridApi } from "tap/components/AGGrid/hooks.js";
import { useLicensingJobsData } from "../../hooks/useLicensingJobsData";
import { useJobsGridUtils } from "./utils/gridUtils";
import { useLoading } from "tap/utils/hooks/useLoading.js";
import { useToasts } from "tap/utils/hooks/useToasts.js";
import { useConfirmationDialog } from "tap/components/dialogs/dialogs/confirmation.jsx";
import { usePermissions } from "tap/utils/permissions.js";
import { tapApi } from "tap/api.js";
import { exportToCsvWithHeaders } from "tap/utils/exportToCsv.js";
import DataGrid from "@dtci/spellbook/DataGrid";
import Banner from "@dtci/spellbook/Banner";
import Typography from "@dtci/spellbook/Typography";
import ContentBlock from "@dtci/spellbook/ContentBlock";
import Button from "@dtci/spellbook/Button";
import Moment from "moment-timezone";
import styles from "../../../../components/AGGrid/details-table.module.scss";
import "./styles.scss";
import { useOffersActions } from "tap/store/offers/useOffers.js";
import { getLicensingInactivateErrorMessage } from "tap/pages/LicensingJobs/utils/inactivateLicensingJobResult.js";
import { usePaginatedGridSelection } from "tap/utils/hooks/usePaginatedGridSelection.js";
import { getAllItemsRange } from "tap/utils/pagination.js";

/**
 * Licensing Jobs Grid - Displays licensing jobs with actions for run, copy, view offers, and status tracking.
 */
export const JobsGrid = () => {
  const navigate = useNavigate();
  const agGridApi = useAgGridApi();
  const { setLoadingTrue, setLoadingFalse } = useLoading();
  const { openConfirmationDialog } = useConfirmationDialog();
  const { showErrorToast, showSuccessToast } = useToasts();
  const { isTapAdmin } = usePermissions();

  const { fetchLicensingJobs, updateJobs, licJobRange, filterForm } =
    useLicensingJobsData();
  const { updateOffers } = useOffersActions();

  const { jobs, refreshJobs, totalItems } = useSelector((s) => s.licensingJobs);

  const fetchAllRows = useCallback(async () => {
    if (!totalItems) {
      return [];
    }
    const data = await fetchLicensingJobs(
      getAllItemsRange(totalItems),
      filterForm,
      null,
      true,
    );
    return data?.content ?? [];
  }, [fetchLicensingJobs, filterForm, totalItems]);

  const {
    bannerText,
    onSelectionChanged,
    onRowSelected,
    clearSelection,
    resolveSelectedRows,
  } = usePaginatedGridSelection({
    totalItems,
    fetchAllRows,
    entityName: "item",
    rowData: jobs ?? [],
    gridApiRef: agGridApi.AgGridApiRef,
    resetKey: `${totalItems}-${licJobRange}-${JSON.stringify(filterForm)}`,
  });

  const { columns } = useJobsGridUtils({
    columnHandlers: [
      (data) => onOpenViewLicensingJob(data),
      (data) => onOpenEditLicensingJob(data),
      (data) => onRunLicensingJob(data),
      (data) => onCheckForUpdates(data),
      (data) => onViewLicensingJobStatus(data),
      (data) => onViewOffers(data),
      (data) => onCopyLicensingJob(data),
      (data) => onInactivateLicensingJob(data),
    ],
    isTapAdmin,
  });

  const onOpenViewLicensingJob = async (job) => {
    setLoadingTrue();
    try {
      const [data, historyData] = await Promise.all([
        tapApi.licensingJobById(job.id),
        tapApi.licensingJobHistory(job.id),
      ]);

      if (data?.code || data?.httpCode || data?.error) {
        showErrorToast(
          data?.message || "Failed to load licensing job details.",
        );
        return;
      }

      const mappedHistory = Array.isArray(historyData)
        ? historyData.map((h) => ({
            label: `${Moment(Moment.utc(h.historyCreatedAt).toDate())
              .tz(Moment.tz.guess())
              .format(
                "MM/DD/YYYY, h:mm:ss A z",
              )} | ${h.historyCreatedBy?.username ?? ""}`,
            value: h.id,
            data: h,
          }))
        : [];

      updateJobs({
        selectedJob: data,
        selectedJobHistory: mappedHistory,
        isJobViewDrawerOpen: true,
      });
    } catch (error) {
      showErrorToast("Failed to load licensing job details.");
      console.error("Error loading licensing job details:", error);
    } finally {
      setLoadingFalse();
    }
  };

  const onOpenEditLicensingJob = async (job) => {
    setLoadingTrue();
    try {
      const data = await tapApi.licensingJobById(job.id);
      if (data?.code || data?.httpCode || data?.error) {
        showErrorToast(data?.message || "Failed to load licensing job.");
        return;
      }
      updateJobs({
        selectedJob: data,
        isEditDrawerOpen: true,
      });
    } catch (error) {
      showErrorToast("Failed to load licensing job.");
      console.error("Error fetching licensing job:", error);
    } finally {
      setLoadingFalse();
    }
  };

  /** Fetches offers and navigates to offers page. Reference data loads lazily on Offers page. */
  const onViewOffers = async (job) => {
    setLoadingTrue();
    try {
      const offers = await tapApi.licensingJobOfferTable(job.id);

      updateJobs({
        selectedJob: job,
      });

      updateOffers({
        offers: Array.isArray(offers) ? offers : [],
        selectedJob: job,
      });

      navigate(`/offers?jobId=${job.id}`);
    } catch (error) {
      showErrorToast("Failed to load offers data.");
      console.error("Error loading offers:", error);
      setLoadingFalse();
    }
  };

  const refreshJobsList = React.useCallback(async () => {
    const data = await fetchLicensingJobs(licJobRange, filterForm, null, true);

    if (data) {
      updateJobs({
        jobs: data?.content,
        totalItems: data?.total,
        totalPages: Math.ceil(data?.total / data?.size) || 0,
        currentPage: data?.pageNumber,
      });
    }
  }, [fetchLicensingJobs, licJobRange, filterForm, updateJobs]);

  const onRunLicensingJob = (job) => {
    openConfirmationDialog(
      "Run Job",
      "Yes",
      ["Are you sure you wish to run the licensing job?"],
      async () => {
        setLoadingTrue();
        try {
          const result = await tapApi.runLicensingJob(job.id);
          if (result === true) {
            await refreshJobsList();
            showSuccessToast("Job running");
          } else {
            showErrorToast("Job cannot be run");
          }
        } catch (error) {
          showErrorToast("Job cannot be run");
          console.error("Error running licensing job:", error);
        } finally {
          setLoadingFalse();
        }
      },
    );
  };

  const onBulkRunLicensingJobs = async () => {
    const selectedRows = await resolveSelectedRows();
    if (selectedRows.length === 0) {
      return;
    }
    const isSingle = selectedRows.length === 1;
    openConfirmationDialog(
      isSingle ? "Run Licensing Job" : "Run Licensing Jobs",
      "Confirm",
      [
        isSingle
          ? "Are you sure you want to run this licensing job?"
          : "Are you sure you want to run the selected licensing jobs?",
      ],
      async () => {
        setLoadingTrue();
        try {
          const ids = selectedRows.map((row) => row.id);
          const result = await tapApi.runLicensingJobBulk(ids);
          if (result?.code || result?.httpCode || result?.error) {
            showErrorToast(
              "Some Licensing Job(s), cannot start to run, probably with inactived status",
            );
          } else {
            showSuccessToast("Success, Job(s) are now running");
          }
          await refreshJobsList();
        } catch (error) {
          showErrorToast("Error: licensing jobs cannot start to run");
          console.error("Error running licensing job(s):", error);
        } finally {
          handleClose();
          setLoadingFalse();
        }
      },
    );
  };

  const onCheckForUpdates = (job) => {
    openConfirmationDialog(
      "Check For Updates",
      "Yes",
      ["Are you sure you wish to check for updates on the licensing job?"],
      async () => {
        setLoadingTrue();
        try {
          const result = await tapApi.checkForUpdates(job.id);
          if (result === true) {
            await refreshJobsList();
            showSuccessToast("Job running");
          } else {
            showErrorToast(result?.message || "Error checking for updates");
          }
        } catch (error) {
          showErrorToast("Error checking for updates");
          console.error("Error checking for updates:", error);
        } finally {
          setLoadingFalse();
        }
      },
    );
  };

  const onViewLicensingJobStatus = (job) => {
    updateJobs({
      selectedJob: job,
      isJobViewModalOpen: true,
      jobViewTasks: [],
      jobViewCurrentPage: 0,
    });
  };

  const handleClose = () => {
    clearSelection();
  };

  useEffect(() => {
    if (refreshJobs) {
      refreshJobsList();
      updateJobs({ refreshJobs: false });
    }
  }, [refreshJobs, refreshJobsList, updateJobs]);

  const onCopyLicensingJob = (job) => {
    openConfirmationDialog(
      "Copy Job",
      "Yes",
      ["Are you sure you wish to copy the licensing job?"],
      async () => {
        setLoadingTrue();
        try {
          const result = await tapApi.copyLicensingJob(job.id);
          if (result?.code || result?.httpCode || result?.error) {
            showErrorToast("Error");
          } else {
            await refreshJobsList();
            showSuccessToast("Success, Job was copied");
          }
        } catch (error) {
          showErrorToast("Error");
          console.error("Error copying licensing job:", error);
        } finally {
          setLoadingFalse();
        }
      },
    );
  };

  const onBulkCopyLicensingJobs = async () => {
    const selectedRows = await resolveSelectedRows();
    if (selectedRows.length === 0) {
      return;
    }
    const isSingle = selectedRows.length === 1;
    openConfirmationDialog(
      isSingle ? "Copy Licensing Job" : "Copy Licensing Jobs",
      "Confirm",
      [
        isSingle
          ? "Are you sure you want to copy this licensing job?"
          : "Are you sure you want to copy the selected licensing jobs?",
      ],
      async () => {
        setLoadingTrue();
        try {
          const ids = selectedRows.map((row) => row.id);
          const responses = await tapApi.copyLicensingJobBulk(ids);

          const successful = [];
          const failed = [];
          for (const r of responses) {
            if (r.statusCodeValue === 200) {
              successful.push(r);
            } else {
              try {
                const body = JSON.parse(r.body);
                failed.push(body.message || body.statusText || "Unknown error");
              } catch {
                failed.push(r.body || "Unknown error");
              }
            }
          }
          if (successful.length > 0) {
            showSuccessToast("Licensing Job(s) copied");
          }
          if (failed.length > 0) {
            showErrorToast(`\n${failed.join("\n")}`);
          }
          await refreshJobsList();
        } catch (error) {
          showErrorToast("Error: licensing jobs not copied");
          console.error("Error copying licensing job(s):", error);
        } finally {
          handleClose();
          setLoadingFalse();
        }
      },
    );
  };

  const onInactivateLicensingJob = (job) => {
    openConfirmationDialog(
      "Inactivate",
      "Yes",
      ["Are you sure you wish to inactive the licensing job?"],
      async () => {
        setLoadingTrue();
        try {
          const result = await tapApi.inactivateLicensingJob(job.id);
          const err = getLicensingInactivateErrorMessage(result);
          if (err) {
            showErrorToast(err);
          } else {
            await refreshJobsList();
            showSuccessToast("Job was Inactivated");
          }
        } catch (error) {
          showErrorToast("Job is running, cannot be inactivated!");
          console.error("Error inactivating licensing job:", error);
        } finally {
          setLoadingFalse();
        }
      },
    );
  };

  const onBulkInactivateLicensingJobs = async () => {
    const selectedRows = await resolveSelectedRows();
    if (selectedRows.length === 0) {
      return;
    }
    const isSingle = selectedRows.length === 1;
    openConfirmationDialog(
      isSingle ? "Inactivate Licensing Job" : "Inactivate Licensing Jobs",
      "Confirm",
      [
        isSingle
          ? "Are you sure you want to inactivate this licensing job?"
          : "Are you sure you want to inactivate the selected licensing jobs",
      ],
      async () => {
        setLoadingTrue();
        try {
          const ids = selectedRows.map((row) => row.id);
          const responses = await tapApi.inactivateLicensingJobBulk(ids);
          const successful = [];
          const failed = [];
          for (const r of responses) {
            if (r.statusCodeValue === 200) {
              successful.push(r);
            } else {
              try {
                const body = JSON.parse(r.body);
                failed.push(body.message || body.statusText || "Unknown error");
              } catch {
                failed.push(r.body || "Unknown error");
              }
            }
          }
          if (successful.length > 0) {
            showSuccessToast("Licensing Job(s) inactivated");
          }
          if (failed.length > 0) {
            showErrorToast(`\n${failed.join("\n")}`);
          }
          await refreshJobsList();
        } catch (error) {
          showErrorToast("Error: licensing jobs not inactivated");
          console.error("Error inactivating licensing job(s):", error);
        } finally {
          handleClose();
          setLoadingFalse();
        }
      },
    );
  };

  const handleExport = async () => {
    const selectedRows = await resolveSelectedRows();
    if (selectedRows.length === 0) {
      return;
    }
    setLoadingTrue();
    try {
      const ids = selectedRows.map((row) => row.id);
      const data = await tapApi.exportLicensingJob(ids);

      if (data?.code || data?.httpCode || data?.error) {
        showErrorToast(
          data?.message || "Failed to export licensing jobs data.",
        );
        return;
      }

      const exportData = (data?.content || []).map((item) => ({
        ...item,
        catalogs: Array.isArray(item.catalogs)
          ? item.catalogs.map((c) => c.titleWithId).join(" | ")
          : item.catalogs,
        medias: Array.isArray(item.medias)
          ? item.medias.map((m) => m.name).join(" | ")
          : item.medias,
        territories: Array.isArray(item.territories)
          ? item.territories.map((t) => t.name).join(" | ")
          : item.territories,
        licenseeTypeList: Array.isArray(item.licenseeTypeList)
          ? item.licenseeTypeList.join(" | ")
          : item.licenseeTypeList,
        createdBy: item.createdBy?.fullName || "",
        modifiedBy: item.modifiedBy?.fullName || "",
      }));

      const headers = [
        "id",
        "description",
        "catalogs",
        "medias",
        "territories",
        "licenseeTypeList",
        "startDate",
        "endDate",
        "status",
        "lastRun",
        "completedAt",
        "createdBy",
        "createdAt",
        "modifiedBy",
        "modifiedAt",
      ];
      const renamedHeaders = [
        "Id",
        "Description",
        "Catalogs",
        "Medias",
        "Territories",
        "Licensee Type",
        "Start Date",
        "End Date",
        "Status",
        "Lastest Job",
        "Completed",
        "Created By",
        "Created",
        "Updated By",
        "Modified",
      ];
      exportToCsvWithHeaders(exportData, headers, renamedHeaders, "licensing");
    } catch (error) {
      showErrorToast("Error exporting licensing jobs.");
      console.error("Error exporting licensing job(s):", error);
    } finally {
      setLoadingFalse();
    }
  };

  return (
    <>
      {bannerText && (
        <Banner
          faPackage="far"
          iconTypePrimary={"fa-check-circle"}
          role="status"
          type={"success"}
          onClose={handleClose}
          className="selection-banner"
        >
          <ContentBlock className="banner-content">
            <Typography className="main-banner-text" variant="T20">
              {bannerText}
            </Typography>
            <div className="banner-actions">
              <Button
                className="bulk-action-btn"
                iconType="fa-solid fa-copy"
                type="button"
                styleType="link"
                onClick={onBulkCopyLicensingJobs}
              >
                COPY
              </Button>
              <Button
                className="bulk-action-btn"
                iconType="fa-solid fa-play"
                type="button"
                styleType="link"
                onClick={onBulkRunLicensingJobs}
              >
                RUN
              </Button>
              {isTapAdmin && (
                <Button
                  className="bulk-action-btn-danger"
                  iconType="fa-solid fa-trash"
                  type="button"
                  styleType="link"
                  onClick={onBulkInactivateLicensingJobs}
                >
                  INACTIVATE
                </Button>
              )}
              <Button
                className="bulk-action-btn"
                iconType="fa-solid fa-arrow-down-to-line"
                type="button"
                styleType="link"
                onClick={handleExport}
              >
                EXPORT
              </Button>
            </div>
          </ContentBlock>
        </Banner>
      )}
      <DataGrid
        className={styles.dataGrid}
        autoSizing
        agGrid={{
          ref: agGridApi.AgGridApiRef,
          rowData: jobs ?? [],
          headerHeight: 45,
          // Allow native browser context menu on links (e.g. Offers → open in new tab)
          suppressContextMenu: true,
          suppressColumnStateReset: true,
          onSelectionChanged,
          onRowSelected,
          rowHeight: 130,
          getRowId: (params) => String(params.data.id),
          suppressScrollOnNewData: true,
        }}
        bordered="none"
        columnDefs={columns}
        defaultColumnDef={{
          resizable: true,
          wrapText: true,
          autoHeight: true,
          suppressHeaderMenuButton: true,
          cellStyle: {
            paddingTop: "10px",
            paddingBottom: "10px",
            wordBreak: "break-word",
          },
        }}
        sortable={false}
        striped
        selectable
        noResultsMessage={"No Jobs Found"}
      />
    </>
  );
};
